import React, { useEffect, useRef, useState } from 'react'
import { View, Text, Icon } from '@/components'
import { createStyles } from '@codeleap/styles'
import { Chat } from '@/config'
import { Channel } from 'stream-chat'
import { ChatLocalMesage } from '@packages/chat'
import { Getstream } from '@/services'

export type PinnedMessageMenuProps = {
  isOpen: boolean
  onClose: () => void
  channel: Channel
  message: ChatLocalMesage
  anchorRef?: React.RefObject<HTMLElement>
}

export const PinnedMessageMenu = (props: PinnedMessageMenuProps) => {
  const { isOpen, onClose, channel, message, anchorRef } = props
  const menuRef = useRef<HTMLDivElement>(null)
  const { isAdmin, isGroup } = Getstream.Chat.useMyMembership(channel)
  const [position, setPosition] = useState({ top: 0, left: 0 })
  const { openPinnedMessage } = Getstream.Chat.usePinnedMessageNavigation()

  useEffect(() => {
    if (isOpen && anchorRef?.current) {
      const rect = anchorRef.current.getBoundingClientRect()
      const textStartOffset = 76
      setPosition({
        top: rect.bottom + 8,
        left: rect.left + textStartOffset,
      })
    }
  }, [isOpen, anchorRef])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        menuRef.current &&
        anchorRef?.current &&
        !menuRef.current.contains(event.target as Node) &&
        !anchorRef.current.contains(event.target as Node)
      ) {
        onClose()
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen, onClose, anchorRef])

  const handleUnpin = async () => {
    onClose()
    await Chat.Services.togglePinMessage({
      channel,
      message,
    })
  }

  const handleGoToMessage = () => {
    onClose()
    openPinnedMessage(message)
  }

  if (!isOpen) return null

  const actions = []

  // Only show unpin for admins in groups
  if (!isGroup || isAdmin) {
    actions.push({
      id: 'unpin',
      label: 'Unpin',
      icon: 'pin-blocked-02',
      onPress: handleUnpin,
    })
  }

  actions.push({
    id: 'go-to-message',
    label: 'Go to message',
    icon: 'arrow-right',
    onPress: handleGoToMessage,
  })

  return (
    <>
      <View style={styles.overlay} onClick={onClose} />
      <View
        ref={menuRef}
        style={{ ...styles.menu, top: `${position.top}px`, left: `${position.left}px` }}
      >
        {actions.map((action, index) => (
          <React.Fragment key={action.id}>
            {index > 0 && <View style={styles.separator} />}
            <View
              style={styles.menuItem}
              onClick={(e) => {
                e.stopPropagation()
                action.onPress()
              }}
            >
              <Text text={action.label} style={['medium', styles.menuItemText]} />
              <Icon name={action.icon as any} style={styles.menuItemIcon} />
            </View>
          </React.Fragment>
        ))}
      </View>
    </>
  )
}

const styles = createStyles(theme => ({
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 999,
    backgroundColor: 'transparent',
  },
  menu: {
    position: 'fixed',
    zIndex: 1000,
    backgroundColor: '#fff',
    borderRadius: theme.radius.regular,
    boxShadow: '0px 4px 16px rgba(0, 0, 0, 0.12)',
    minWidth: 200,
    maxWidth: 280,
    overflow: 'hidden',
    border: `1px solid ${theme.baseColors.neutralBlackTransparent100}`,
    display: 'flex',
    flexDirection: 'column',
  },
  menuItem: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing.value(1),
    padding: `${theme.spacing.value(1.5)}px ${theme.spacing.value(2)}px`,
    cursor: 'pointer',
    transition: 'background-color 0.2s ease',

    '&:hover': {
      backgroundColor: theme.colors.rowBgHover,
    },
  },
  menuItemText: {
    fontSize: 14,
    color: theme.colors.textHeadingRegular,
  },
  menuItemIcon: {
    ...theme.sized(2.5),
    color: theme.colors.textHeadingRegular,
  },
  separator: {
    width: '100%',
    height: 1,
    backgroundColor: theme.baseColors.neutralBlackTransparent100,
  },
}))
